import { observable } from "@legendapp/state";
import type { RunProgressEvent } from "@repo/shared/types/run-progress";

/** Live counters for a dataset's in-flight streaming publish. */
export interface RunProgress {
  runId: string;
  /** Epoch ms the tracked run started, used to rank it against other runs. */
  startedAt: number;
  rowCount: number;
  sizeBytes: number;
}

/**
 * Transient progress for streaming publishes, keyed by dataset id. Never
 * persisted and never synced — entries only exist between `run-progress`
 * broadcasts and the dataset leaving `RUNNING`.
 *
 * Spinner sites read this to show what a long publish has moved so far; absence
 * of an entry (a buffered driver, a short publish, a page refresh mid-stream) is
 * normal and falls back to the plain spinner.
 */
export const runProgress$ = observable<Record<string, RunProgress>>({});

/**
 * Applies an incoming progress broadcast, keeping the newest run's counters.
 *
 * Two runs for the same dataset can stream at once — a retried publish whose
 * predecessor is still transferring — and their ticks interleave on the org
 * channel. Taking each tick as it arrives would make the counter alternate
 * between two unrelated totals, so a tick only lands if it belongs to the run
 * being tracked or to a newer one.
 */
export const recordRunProgress = ({
  id,
  runId,
  // A tick from a server that predates this field reads as the oldest possible
  // run, which keeps the behavior last-write-wins the way it used to be rather
  // than letting an unknown age outrank a known one.
  startedAt = 0,
  rowCount,
  sizeBytes,
}: RunProgressEvent) => {
  const current = runProgress$[id].peek();

  if (current) {
    // Ably does not guarantee ordering, so a run's own late tick would
    // otherwise walk its counters backwards.
    if (current.runId === runId) {
      if (current.rowCount > rowCount) return;
    } else if (startedAt < current.startedAt) {
      // An older run than the one being tracked: it has been superseded, and
      // its counters describe a transfer nothing is waiting on.
      return;
    }
  }

  runProgress$[id].set({ runId, startedAt, rowCount, sizeBytes });
};

/** Drops a dataset's counters once its publish is no longer running. */
export const clearRunProgress = (datasetId: string) => {
  if (runProgress$[datasetId].peek() === undefined) return;
  runProgress$[datasetId].delete();
};
